/** GxMenu: a button that opens a short list of actions ("Countdown ▾", "⋯"). Esc or an outside click closes it. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { isMobileBand, useBand } from "./breakpoints";
import { GxSheet } from "./GxSheet";
import "./menu.css";

export interface MenuItem { readonly key: string; readonly label: string; readonly onSelect: () => void; readonly tone?: "danger"; /** One line under the label saying what the action does. */ readonly hint?: string }

export function GxMenu({ label, trigger, items, className = "gx-btn gx-btn--secondary", testId, align = "left", heading }: {
  /** Who the menu is for: shown above the actions, not a choice. */ readonly heading?: ReactNode;
  readonly label: string; readonly trigger: ReactNode; readonly items: readonly MenuItem[]; readonly className?: string; readonly testId?: string; readonly align?: "left" | "right";
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger_ = useRef<HTMLButtonElement>(null);
  // On a phone or small tablet the list is a bottom sheet with 48 px rows; the sheet closes itself on a scrim tap, so no outside-click listener.
  const sheet = isMobileBand(useBand());

  useEffect(() => {
    if (!open || sheet) return;
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open, sheet]);

  return (
    <div className="gx-menu" ref={root} onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}>
      <button ref={trigger_} type="button" className={className} aria-label={label} aria-haspopup="menu" aria-expanded={open} data-testid={testId} onClick={() => setOpen((o) => !o)}>{trigger}</button>
      {open && sheet ? (
        <GxSheet title={label} onClose={() => setOpen(false)} invoker={trigger_.current}>
          {heading === undefined ? null : <div className="gx-menu-heading" role="presentation">{heading}</div>}
          <div role="menu" aria-label={label}>
            {items.map((it) => (
              <button key={it.key} type="button" role="menuitem" className="gx-menu-item" data-tone={it.tone} onClick={() => { setOpen(false); it.onSelect(); }}>{it.label}{it.hint === undefined ? null : <small className="gx-menu-hint">{it.hint}</small>}</button>
            ))}
          </div>
        </GxSheet>
      ) : open ? (
        <div className="gx-menu-list" role="menu" data-align={align}>
          {heading === undefined ? null : <div className="gx-menu-heading" role="presentation">{heading}</div>}
          {items.map((it) => (
            <button key={it.key} type="button" role="menuitem" className="gx-menu-item" data-tone={it.tone} onClick={() => { setOpen(false); it.onSelect(); }}>{it.label}{it.hint === undefined ? null : <small className="gx-menu-hint">{it.hint}</small>}</button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
